import * as React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import i18n from 'modules/core/lib/i18n'

import type Period from 'modules/attendance/store/models/Period'

import ErrorList from './errors/ErrorList'

export interface CompensationResult {
  employee_id: number
  error?: string
}

export interface CompensationReponse {
  successes: CompensationResult[]
  errors: CompensationResult[]
}

export type CompensationRequestAction = 'create' | 'destroy'

export interface Props {
  action: CompensationRequestAction
  response: CompensationReponse
  selected: Period[]
  onCloseModal: () => void
}

export interface CompensationReponse {
  successes: CompensationResult[]
  errors: CompensationResult[]
}

interface ActionTranslationsProps {
  title: string
  successDescription: (count: number) => string
  errorDescription: (count: number) => string
}

const actionTranslations = (action: CompensationRequestAction): ActionTranslationsProps => {
  switch (action) {
    case 'create':
      return {
        title: i18n.t(`time_tracking.modals.compensation.payroll.response.create.title`),
        successDescription: (count: number) =>
          i18n.tp('time_tracking.modals.compensation.payroll.response.create.success_description', {
            count,
          }),
        errorDescription: (count: number) =>
          i18n.tp('time_tracking.modals.compensation.payroll.response.create.error_description', {
            count,
          }),
      }
    case 'destroy':
      return {
        title: i18n.t(`time_tracking.modals.compensation.payroll.response.destroy.title`),
        successDescription: (count: number) =>
          i18n.tp(
            'time_tracking.modals.compensation.payroll.response.destroy.success_description',
            {
              count,
            }
          ),
        errorDescription: (count: number) =>
          i18n.tp('time_tracking.modals.compensation.payroll.response.destroy.error_description', {
            count,
          }),
      }
    default:
      throw new Error(`Unknown action: ${action}`)
  }
}

const ResponseModal = ({ action, response, selected, onCloseModal }: Props) => {
  const uniqueSuccessEmployeeIds = new Set(response.successes.map((success) => success.employee_id))
    .size
  const uniqueErrorEmployeeIds = new Set(response.errors.map((error) => error.employee_id)).size

  const { title, successDescription, errorDescription } = actionTranslations(action)
  return (
    <Modal
      title={title}
      illustration="cash"
      footer={
        <Button onClick={onCloseModal}>
          {i18n.t('time_tracking.modals.compensation.payroll.response.ok')}
        </Button>
      }
    >
      <Box justifyContent="flexStart" alignItems="flexStart" gap="s40" width="full">
        {response.successes.length > 0 && (
          <Text.Normal>{successDescription(uniqueSuccessEmployeeIds)}</Text.Normal>
        )}
        {response.errors.length > 0 && (
          <Box justifyContent="flexStart" alignItems="flexStart" gap="s12" width="full">
            <Text.Normal>{errorDescription(uniqueErrorEmployeeIds)}</Text.Normal>
            <ErrorList errors={response.errors} selected={selected} />
          </Box>
        )}
      </Box>
    </Modal>
  )
}

export default observer(ResponseModal)
